import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const AssessmentCompletionRouter = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [routing, setRouting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (assessmentId && assessmentId !== 'undefined') {
      // Redirect directly to enhanced results
      navigate(`/assessment/results/${assessmentId}`);
    } else {
      setError('Invalid assessment ID');
      setLoading(false);
    }
  }, [assessmentId, navigate]);

  const handleProceed = () => {
    if (routing?.routing_recommendation?.url) {
      navigate(routing.routing_recommendation.url);
    }
  };

  const handleViewResults = () => {
    navigate('/assessment/results');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Determining your next steps...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Unable to Determine Next Steps</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={handleViewResults}
              className="bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-700 transition-colors"
            >
              View Assessment Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!routing?.routing_recommendation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No routing recommendation available</p>
          <button
            onClick={handleViewResults}
            className="mt-4 bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-700 transition-colors"
          >
            View Assessment Results
          </button>
        </div>
      </div>
    );
  }

  const { routing_recommendation } = routing;
  const isEMRA = routing_recommendation.route_to === 'EMRA';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Assessment Complete!</h1>
            <p className="text-gray-600">
              Thank you for completing your Energy Savings Assessment.
            </p>
          </div>

          {/* Energy Spend Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex items-center justify-center">
              <div className="text-center">
                <p className="text-sm text-blue-700">Annual Energy Spend</p>
                <p className="text-2xl font-bold text-blue-900">
                  ${routing.annual_energy_spend?.toLocaleString() || '0'}
                </p>
              </div>
            </div>
          </div>

          {/* Routing Recommendation */}
          <div className="border-l-4 border-emerald-500 pl-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Recommended Next Step: {routing_recommendation.assessment_type}
            </h2>
            <p className="text-gray-600 mb-4">{routing_recommendation.reason}</p>
            <p className="text-gray-700">{routing_recommendation.description}</p>
          </div>

          {/* Assessment Details */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">
                {isEMRA ? 'EMRA Includes:' : 'EMHA Includes:'}
              </h3>
              <ul className="space-y-2">
                {routing_recommendation.includes?.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="h-5 w-5 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Assessment Type</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Route to:</span>
                  <span className="text-sm font-medium text-gray-900">{routing_recommendation.route_to}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Next Action:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {routing_recommendation.next_action === 'book_consultation' ? 'Book Consultation' : 'Start Assessment'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Type:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {isEMRA ? 'Strategic Consultation' : 'Online Assessment'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleProceed}
              className="flex-1 sm:flex-none bg-emerald-600 text-white px-8 py-3 rounded-md font-medium hover:bg-emerald-700 transition-colors text-center"
            >
              {isEMRA ? 'Book EMRA Consultation' : 'Start EMHA Assessment'}
            </button>
            
            <button
              onClick={handleViewResults}
              className="flex-1 sm:flex-none bg-white border border-gray-300 text-gray-700 px-8 py-3 rounded-md font-medium hover:bg-gray-50 transition-colors text-center"
            >
              View ESA Results
            </button>
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              {isEMRA 
                ? 'EMRA consultations are recommended for high energy spend facilities requiring strategic planning.'
                : 'EMHA assessments help determine your energy management readiness and capability gaps.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentCompletionRouter;